/**
 * Storage utility dengan encoding/decoding base64 untuk data sensitif
 */

// Keys constants untuk menghindari typo
export const STORAGE_KEYS = {
  LAST_PAYMENT: "app_lastPayment",
  PAYMENT_SUCCESS: "app_paymentSuccess",
  PAYMENT_ORDER_DEADLINE: "app_payment_order_deadline",
  HAS_ACTIVE_ORDER: "app_has_active_order",
  ACTIVE_ORDER: "app_active_order",
  CURRENT_INVOICE: "app_currentInvoice",
  LAST_ORDER_STATUS: "app_last_order_status",
  LAST_ORDER: "app_last_order",
  ORDER_DEADLINE: "app_order_deadline",
  CUSTOMER: "app_customer",
  PAYMENT_DEADLINE: "app_payment_deadline",
  HISTORY_ORDER: "app_history-order",
  WA_NUMBER: "app_wa_number",
  /** Prefix for per-outlet product catalog cache: app_products_cache_{outletId} */
  PRODUCTS_CACHE_PREFIX: "app_products_cache_",
  /** Prefix for per-outlet top picks cache: app_top_picks_cache_{outletId} */
  TOP_PICKS_CACHE_PREFIX: "app_top_picks_cache_",
  /** Product highlight tags (IDs only) from /v1/product/tag */
  PRODUCT_TAGS: "app_product_tags",
};

/** Default TTL for product catalog & top picks (10 minutes) */
export const PRODUCT_CACHE_TTL_SECONDS = 10 * 60;

export function productsCacheKey(outletId) {
  return `${STORAGE_KEYS.PRODUCTS_CACHE_PREFIX}${outletId}`;
}

export function topPicksCacheKey(outletId) {
  return `${STORAGE_KEYS.TOP_PICKS_CACHE_PREFIX}${outletId}`;
}

/**
 * Encode string ke base64
 */
export function encodeBase64(str) {
  try {
    return btoa(str);
  } catch (error) {
    console.error("Error encoding to base64:", error);
    return str;
  }
}

/**
 * Decode base64 ke string
 */
export function decodeBase64(encodedStr) {
  try {
    return atob(encodedStr);
  } catch (error) {
    console.error("Error decoding from base64:", error);
    return encodedStr;
  }
}

/**
 * Simpan data ke localStorage dengan opsional encoding
 */
export function setStorageItem(key, value, encode = false) {
  try {
    let processedValue = value;

    if (encode && typeof value === "string") {
      processedValue = encodeBase64(value);
    } else if (typeof value === "object") {
      processedValue = JSON.stringify(value);
    }

    localStorage.setItem(key, processedValue);
  } catch (error) {
    console.error(`Error setting localStorage item ${key}:`, error);
  }
}

/**
 * Ambil data dari localStorage dengan opsional decoding
 */
export function getStorageItem(key, decode = false, isJson = false) {
  try {
    const value = localStorage.getItem(key);

    if (value === null) return null;

    let processedValue = value;

    if (decode && typeof value === "string") {
      processedValue = decodeBase64(value);
    } else if (isJson) {
      processedValue = JSON.parse(value);
    }

    return processedValue;
  } catch (error) {
    console.error(`Error getting localStorage item ${key}:`, error);
    return null;
  }
}

/**
 * Hapus item dari localStorage
 */
export function removeStorageItem(key) {
  try {
    localStorage.removeItem(key);
  } catch (error) {
    console.error(`Error removing localStorage item ${key}:`, error);
  }
}

/**
 * Hapus multiple items dari localStorage
 */
export function removeStorageItems(keys) {
  keys.forEach((key) => removeStorageItem(key));
}

/**
 * Simpan data ke localStorage dengan masa berlaku (TTL).
 * @param {string} key
 * @param {*} value - akan di-JSON.stringify
 * @param {number} ttlSeconds - detik dari sekarang
 */
export function setWithExpiry(key, value, ttlSeconds) {
  const now = Math.floor(Date.now() / 1000);
  const payload = {
    data: value,
    expiresAt: now + Number(ttlSeconds),
  };
  setStorageItem(key, payload);
}

/**
 * Ambil data ber-TTL dari localStorage.
 * @param {string} key
 * @param {{ allowStale?: boolean }} [options]
 * @returns {{ data: *, stale: boolean, expiresAt: number } | null}
 *   - null jika tidak ada / invalid / expired (kecuali allowStale)
 *   - stale: true jika expired tapi allowStale=true
 */
export function getWithExpiry(key, options = {}) {
  const { allowStale = false } = options;
  try {
    const raw = localStorage.getItem(key);
    if (raw === null) return null;

    const payload = JSON.parse(raw);
    if (
      !payload ||
      typeof payload !== "object" ||
      typeof payload.expiresAt !== "number" ||
      !("data" in payload)
    ) {
      removeStorageItem(key);
      return null;
    }

    const now = Math.floor(Date.now() / 1000);
    const isExpired = now >= payload.expiresAt;

    if (isExpired) {
      // Keep expired payload in storage so callers can opt into
      // allowStale as a network-failure fallback (do not delete here).
      if (allowStale) {
        return {
          data: payload.data,
          stale: true,
          expiresAt: payload.expiresAt,
        };
      }
      return null;
    }

    return {
      data: payload.data,
      stale: false,
      expiresAt: payload.expiresAt,
    };
  } catch (error) {
    console.error(`Error getting localStorage item with expiry ${key}:`, error);
    return null;
  }
}

/**
 * Simpan currentInvoice dengan encoding base64
 */
export function setCurrentInvoice(invoice) {
  setStorageItem(STORAGE_KEYS.CURRENT_INVOICE, invoice, true);
}

/**
 * Ambil currentInvoice dengan decoding base64
 */
export function getCurrentInvoice() {
  return getStorageItem(STORAGE_KEYS.CURRENT_INVOICE, true);
}

/**
 * Fungsi-fungsi khusus untuk data lainnya
 */
export const storage = {
  // Payment data
  setLastPayment: (payment) =>
    setStorageItem(STORAGE_KEYS.LAST_PAYMENT, payment, true),
  getLastPayment: () => getStorageItem(STORAGE_KEYS.LAST_PAYMENT, true, true),

  // Order data
  setActiveOrder: (order) =>
    setStorageItem(STORAGE_KEYS.ACTIVE_ORDER, order, false, true),
  getActiveOrder: () => getStorageItem(STORAGE_KEYS.ACTIVE_ORDER, false, true),

  // Customer data
  setCustomer: (customer) =>
    setStorageItem(STORAGE_KEYS.CUSTOMER, customer, false, true),
  getCustomer: () => getStorageItem(STORAGE_KEYS.CUSTOMER, false, true),

  // last order
  setLastOrder: (lastOrder) =>
    setStorageItem(STORAGE_KEYS.LAST_ORDER, lastOrder, false, true),
  getLastOrder: () => getStorageItem(STORAGE_KEYS.LAST_ORDER, false, true),

  // wa number
  setWaNumber: (waNumber) =>
    setStorageItem(STORAGE_KEYS.WA_NUMBER, waNumber, false, true),
  getWaNumber: () => getStorageItem(STORAGE_KEYS.WA_NUMBER, false, true),

  // history order
  setHistoryOrder: (historyOrder) =>
    setStorageItem(STORAGE_KEYS.HISTORY_ORDER, historyOrder, false, true),
  getHistoryOrder: () =>
    getStorageItem(STORAGE_KEYS.HISTORY_ORDER, false, true),

  // Status data (boolean/string)
  setPaymentSuccess: (status) =>
    setStorageItem(STORAGE_KEYS.PAYMENT_SUCCESS, status),
  getPaymentSuccess: () => getStorageItem(STORAGE_KEYS.PAYMENT_SUCCESS),

  setHasActiveOrder: (status) =>
    setStorageItem(STORAGE_KEYS.HAS_ACTIVE_ORDER, status),
  getHasActiveOrder: () => getStorageItem(STORAGE_KEYS.HAS_ACTIVE_ORDER),

  setLastOrderStatus: (status) =>
    setStorageItem(STORAGE_KEYS.LAST_ORDER_STATUS, status),
  getLastOrderStatus: () => getStorageItem(STORAGE_KEYS.LAST_ORDER_STATUS),

  // Deadline data (numeric)
  setPaymentOrderDeadline: (deadline) =>
    setStorageItem(STORAGE_KEYS.PAYMENT_ORDER_DEADLINE, deadline),
  getPaymentOrderDeadline: () =>
    getStorageItem(STORAGE_KEYS.PAYMENT_ORDER_DEADLINE),

  setOrderDeadline: (deadline) =>
    setStorageItem(STORAGE_KEYS.ORDER_DEADLINE, deadline),
  getOrderDeadline: () => getStorageItem(STORAGE_KEYS.ORDER_DEADLINE),

  setPaymentDeadline: (deadline) =>
    setStorageItem(STORAGE_KEYS.PAYMENT_DEADLINE, deadline),
  getPaymentDeadline: () => getStorageItem(STORAGE_KEYS.PAYMENT_DEADLINE),

  /**
   * Product catalog cache (per outlet), with TTL.
   * Stores raw API category list (without UI flags).
   */
  setProductCategories: (
    outletId,
    categories,
    ttlSeconds = PRODUCT_CACHE_TTL_SECONDS
  ) => setWithExpiry(productsCacheKey(outletId), categories, ttlSeconds),

  /**
   * @param {string|number} outletId
   * @param {{ allowStale?: boolean }} [options]
   * @returns {{ data: *, stale: boolean, expiresAt: number } | null}
   */
  getProductCategories: (outletId, options = {}) =>
    getWithExpiry(productsCacheKey(outletId), options),

  /**
   * Top picks / recommendations cache (per outlet), with TTL.
   */
  setTopPicks: (
    outletId,
    items,
    ttlSeconds = PRODUCT_CACHE_TTL_SECONDS
  ) => setWithExpiry(topPicksCacheKey(outletId), items, ttlSeconds),

  /**
   * @param {string|number} outletId
   * @param {{ allowStale?: boolean }} [options]
   * @returns {{ data: *, stale: boolean, expiresAt: number } | null}
   */
  getTopPicks: (outletId, options = {}) =>
    getWithExpiry(topPicksCacheKey(outletId), options),

  /**
   * Product highlight tags from /v1/product/tag (product_ids only).
   * Cached globally — resolve against the current outlet catalog in the UI.
   */
  setProductTags: (tags, ttlSeconds = PRODUCT_CACHE_TTL_SECONDS) =>
    setWithExpiry(STORAGE_KEYS.PRODUCT_TAGS, tags, ttlSeconds),

  /**
   * @param {{ allowStale?: boolean }} [options]
   * @returns {{ data: *, stale: boolean, expiresAt: number } | null}
   */
  getProductTags: (options = {}) =>
    getWithExpiry(STORAGE_KEYS.PRODUCT_TAGS, options),

  clearProductCache: (outletId) => {
    removeStorageItem(productsCacheKey(outletId));
    removeStorageItem(topPicksCacheKey(outletId));
  },

  clearProductTagsCache: () => {
    removeStorageItem(STORAGE_KEYS.PRODUCT_TAGS);
  },

  // Remove functions
  remove: (key) => removeStorageItem(key),
  removeMultiple: (keys) => removeStorageItems(keys),
};
